import React, { useCallback, useEffect, useState } from 'react'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import MtlsCaManager from '@devnw/cyber-iam/identity/MtlsCaManager/MtlsCaManager'
import type {
  ClientCaSummary,
  MtlsStatus,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import type { AdminClientCAResp } from '../../../gen/client/react/client'

/**
 * MtlsCAs — system-admin mTLS client-CA trust-store console (Task 17a).
 *
 * OIDCPAM Gate-6 Phase 6c: migrated off MUI onto
 * @devnw/cyber-iam/identity/MtlsCaManager. The component owns the table,
 * per-row enable/disable + two-phase-confirm revoke, and the Add-CA form
 * (PEM textarea + client-side PEM-file upload); this page owns every API
 * call (adminListClientCAs / adminCreateClientCA / adminUpdateClientCA /
 * adminDeleteClientCA / adminMTLSStatus) and the wire-mapping below.
 *
 * WIRE MAPPING: adminListClientCAs's declared TS return type
 * (AdminClientCAListResp) is an empty-interface codegen artifact — the Go
 * handler (admin_tenants_mtls_server.go's AdminListClientCAs) writes a BARE
 * JSON ARRAY of `clientCAResponse` objects directly
 * (`writeSystemAdminJSON(w, 200, items, ...)`), so the generated client's
 * decodeJSON()/fromWire() resolves this call to an already-camelCase
 * Array<AdminClientCAResp> at runtime, not the empty object the declared
 * type claims. adminCreateClientCA / adminGetClientCA / adminUpdateClientCA
 * / adminDeleteClientCA all resolve to that same per-CA shape.
 *
 * LATENT BUG FIXED: the pre-migration page rendered a "Tenant" column by
 * reading `ca.tenant_id` off this already-camelCase response, via an
 * `as unknown as ClientCAConfig[]` cast onto api/types.ts's OLD
 * hand-written snake_case `ClientCAConfig` shape. Neither AdminClientCAResp
 * nor the Go `clientCAResponse` it is generated from (admin_mtls_cas.go)
 * has ever carried a tenant field on ANY client-CA response — so that
 * column silently rendered "global" for every row, even CAs created with a
 * tenantId (AdminCreateClientCAReq.tenantId is accepted and stored, just
 * never echoed back by any list/get response). This page does not
 * reproduce that always-"global" bug: ClientCaSummary.tenantId is left
 * undefined (renders "—" — an honest "unknown", not a wrong "global") until
 * a admin-list response actually carries the tenant back (documented gap,
 * not a regression).
 *
 * `expired` is HOST-COMPUTED from `notAfter` (compared against
 * `Date.now()`) — MtlsCaManager never parses dates. `notAfter`/`createdAt`
 * are pre-formatted display strings (host formats), matching the
 * WebAuthnCredentials.tsx / PasskeyCredentialList `formatCreatedAt`
 * precedent.
 */

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

function isExpired(notAfterIso?: string): boolean {
  if (!notAfterIso) return false
  const t = Date.parse(notAfterIso)
  return !Number.isNaN(t) && t < Date.now()
}

function formatDate(iso?: string): string | undefined {
  if (!iso) return undefined
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function toClientCaSummary(ca: AdminClientCAResp): ClientCaSummary {
  return {
    id: ca.id ?? '',
    label: ca.label ?? '',
    fingerprint: ca.fingerprint ?? '',
    enabled: ca.enabled ?? false,
    // Pre-formatted display strings — the component never parses dates.
    notAfter: formatDate(ca.notAfter),
    createdAt: formatDate(ca.createdAt),
    // HOST-COMPUTED from the RAW (unformatted) notAfter — expiry must be
    // derived before the locale-formatted string discards precision.
    expired: isExpired(ca.notAfter),
  }
}

export const MtlsCAs = (): JSX.Element => {
  const client = useAdminGenClient()
  const [cas, setCas] = useState<ClientCaSummary[]>([])
  const [status, setStatus] = useState<MtlsStatus | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>(undefined)

  const loadCAs = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(undefined)
    try {
      const resp = await client.adminListClientCAs({})
      const raw = (resp as unknown as AdminClientCAResp[]) ?? []
      setCas(raw.map(toClientCaSummary))
    } catch (e: unknown) {
      setError(errMessage(e))
    } finally {
      setLoading(false)
    }
  }, [client])

  const loadStatus = useCallback((): void => {
    client
      .adminMTLSStatus({})
      .then((s) => setStatus({ enabled: s.enabled, count: s.count }))
      .catch(() => {
        // Status banner is informational; a failure is non-fatal.
      })
  }, [client])

  useEffect(() => {
    loadCAs()
      .then(() => {})
      .catch(() => {})
  }, [loadCAs])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const handleRefresh = useCallback((): void => {
    loadCAs()
      .then(() => {})
      .catch(() => {})
    loadStatus()
  }, [loadCAs, loadStatus])

  return (
    <MtlsCaManager
      testID="mtls-cas"
      title="mTLS client CAs"
      cas={cas}
      status={status}
      loading={loading}
      error={error}
      onCreate={async ({ label, pem }) => {
        await client.adminCreateClientCA({ label, pem })
      }}
      onToggle={async (id, enabled) => {
        await client.adminUpdateClientCA({ id, enabled })
      }}
      onRevoke={async (id) => {
        await client.adminDeleteClientCA({ id })
      }}
      onRefresh={handleRefresh}
    />
  )
}
